<template>
  <section class="bg-white shadow p-3 mt-4">
    <div class="d-flex align-items-center justify-content-between mb-3">
      <h2 class="m-0">Benutzer</h2>

      <button class="btn" @click="loadBenutzer()" title="Benutzer neu laden">
        <font-awesome-icon icon="rotate" class="me-2" />
      </button>
    </div>

    <div class="row mx-0 mb-3">
      <div class="col-4">
        <EditBenutzerModal :index="0" :button-style="0" @deleteBenutzer="deleteBenutzer" />
      </div>

      <div class="col-4 my-auto">
        <div class="input-group input-group-sm my-auto">
          <input v-model.trim="searchString" type="search" class="form-control" id="searchInput" placeholder="Suche..." />
          <span class="input-group-text">
            <font-awesome-icon icon="magnifying-glass" class="p-1" />
          </span>
        </div>
      </div>
    </div>

    <LoadingSpinner v-if="loading" />

    <div v-else-if="errorMsg" class="alert alert-danger"><strong class="text-danger">Es ist ein Fehler aufgetreten: </strong> {{ errorMsg }}</div>

    <div v-if="benutzer.length == 0" class="alert alert-info">Es wurden noch keine Benutzer angelegt. Bitte lege einen neuen Benutzer an.</div>

    <div v-else>
      <div class="row mx-0 mb-2 bg-white shadow">
        <div class="col py-2 table-col">
          <div class="dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
            <strong class="me-1">Benutzer</strong>

            <font-awesome-icon icon="arrow-down-long" v-show="sortByValue == 'benutzernameasc'" class="text-secondary table-sort-icon" />
            <font-awesome-icon icon="arrow-up-long" v-show="sortByValue == 'benutzernamedesc'" class="text-secondary table-sort-icon" />

            <ul class="dropdown-menu m-0 ms-1 p-0 rounded-0">
              <li class="dropdown-item select-sort" @click="sortBy('benutzernameasc')">
                <div class="row">
                  <div class="col-2">
                    <font-awesome-icon icon="check" v-show="sortByValue == 'benutzernameasc'" />
                  </div>
                  <div class="col">A - Z</div>
                </div>
              </li>
              <li class="dropdown-item select-sort" @click="sortBy('benutzernamedesc')">
                <div class="row">
                  <div class="col-2">
                    <font-awesome-icon icon="check" v-show="sortByValue == 'benutzernamedesc'" />
                  </div>
                  <div class="col">Z - A</div>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div class="col py-2 table-col">
          <div class="dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
            <strong class="me-1">Username</strong>

            <font-awesome-icon icon="arrow-down-long" v-show="sortByValue == 'usernameasc'" class="text-secondary table-sort-icon" />
            <font-awesome-icon icon="arrow-up-long" v-show="sortByValue == 'usernamedesc'" class="text-secondary table-sort-icon" />

            <ul class="dropdown-menu m-0 ms-1 p-0 rounded-0">
              <li class="dropdown-item select-sort" @click="sortBy('usernameasc')">
                <div class="row">
                  <div class="col-2">
                    <font-awesome-icon icon="check" v-show="sortByValue == 'usernameasc'" />
                  </div>
                  <div class="col">A - Z</div>
                </div>
              </li>
              <li class="dropdown-item select-sort" @click="sortBy('usernamedesc')">
                <div class="row">
                  <div class="col-2">
                    <font-awesome-icon icon="check" v-show="sortByValue == 'usernamedesc'" />
                  </div>
                  <div class="col">Z - A</div>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div class="col py-2 table-col">
          <div class="dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
            <strong class="me-1">E-Mail</strong>

            <font-awesome-icon icon="arrow-down-long" v-show="sortByValue == 'emailasc'" class="text-secondary table-sort-icon" />
            <font-awesome-icon icon="arrow-up-long" v-show="sortByValue == 'emaildesc'" class="text-secondary table-sort-icon" />

            <ul class="dropdown-menu m-0 ms-1 p-0 rounded-0">
              <li class="dropdown-item select-sort" @click="sortBy('emailasc')">
                <div class="row">
                  <div class="col-2">
                    <font-awesome-icon icon="check" v-show="sortByValue == 'emailasc'" />
                  </div>
                  <div class="col">A - Z</div>
                </div>
              </li>
              <li class="dropdown-item select-sort" @click="sortBy('emaildesc')">
                <div class="row">
                  <div class="col-2">
                    <font-awesome-icon icon="check" v-show="sortByValue == 'emaildesc'" />
                  </div>
                  <div class="col">Z - A</div>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div class="col-1 py-2 fw-bold">Rolle</div>
        <div class="col-2 py-2 fw-bold">Signatur</div>
      </div>

      <div class="users-section">
        <div v-for="(user, index) in sortedBenutzer" :key="user.id">
          <BenutzerRow :benutzer="user" :index="index" />
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
  //#region Imports
  import { computed, onMounted, ref } from 'vue';
  import { useStore } from '@/store';

  import { Benutzer } from '@/models/AuthModels';

  import { ActionTypes as AuthActionTypes } from '@/store/modules/Auth/actions';

  import LoadingSpinner from '@/components/LoadingSpinner.vue';
  import EditBenutzerModal from './EditBenutzerModal.vue';
  import BenutzerRow from './BenutzerRow.vue';
  //#endregion

  const store = useStore();

  const benutzer = computed(() => store.getters.benutzer);
  const archivUser = computed(() => store.getters.archivUsers);

  const loading = computed(
    () =>
      store.getters.status.getBenutzerLoading ||
      store.getters.status.createBenutzerLoading ||
      store.getters.status.updateBenutzerLoading ||
      store.getters.status.deleteBenutzerLoading ||
      store.getters.status.getArchivUsersLoading
  );
  const errorMsg = computed(() => store.getters.status.benutzerErrorMsg);

  function loadBenutzer() {
    store.dispatch(AuthActionTypes.GetBenutzer, undefined);
    store.dispatch(AuthActionTypes.GetArchivUsers, undefined);
  }

  function deleteBenutzer(benutzerId: number) {
    store.dispatch(AuthActionTypes.RemoveBenutzer, benutzerId);
  }

  const searchString = ref('');

  const sortByValue = ref('benutzernameasc');
  function sortBy(param: string) {
    sortByValue.value = param;
  }

  const sortedBenutzer = computed(() => {
    return benutzer.value
      .filter((v: Benutzer) => {
        const aUser = archivUser.value.find((u) => u.id === v.identityUserId);

        return (
          aUser?.benutzername?.trim().toLowerCase().includes(searchString.value.trim().toLowerCase()) ||
          aUser?.username?.trim().toLowerCase().includes(searchString.value.trim().toLowerCase()) ||
          aUser?.email?.trim().toLowerCase().includes(searchString.value.trim().toLowerCase())
        );
      })
      .sort((a: Benutzer, b: Benutzer) => {
        const aUser = archivUser.value.find((u) => u.id === a.identityUserId);
        const bUser = archivUser.value.find((u) => u.id === b.identityUserId);

        if (sortByValue.value == 'benutzernamedesc' || sortByValue.value == 'benutzernameasc') {
          var benutzernameComparison =
            aUser?.benutzername
              ?.trim()
              .toLowerCase()
              .localeCompare(bUser?.benutzername?.trim().toLowerCase() || '') || 0;
          if (sortByValue.value == 'benutzernamedesc') {
            benutzernameComparison = -1 * benutzernameComparison;
          }

          return benutzernameComparison;
        }

        if (sortByValue.value == 'usernameasc' || sortByValue.value == 'usernamedesc') {
          var usernameComparison =
            aUser?.username
              ?.trim()
              .toLowerCase()
              .localeCompare(bUser?.username?.trim().toLowerCase() || '') || 0;
          if (sortByValue.value == 'usernamedesc') {
            usernameComparison = -1 * usernameComparison;
          }

          return usernameComparison;
        }

        if (sortByValue.value == 'emailasc' || sortByValue.value == 'emaildesc') {
          var emailComparison =
            aUser?.email
              ?.trim()
              .toLowerCase()
              .localeCompare(bUser?.email?.trim().toLowerCase() || '') || 0;
          if (sortByValue.value == 'emaildesc') {
            emailComparison = -1 * emailComparison;
          }

          return emailComparison;
        }

        return 0;
      });
  });

  onMounted(() => {
    if (benutzer.value.length === 0) {
      loadBenutzer();
    }
  });
</script>

<style scoped lang="scss">
  .users-section {
    min-height: 200px;
    max-height: 400px;
    overflow-y: auto;
  }

  .table-col:hover {
    background: rgb(216, 217, 222);
    cursor: pointer;
  }

  .table-sort-icon {
    font-size: 0.7em;
  }

  .select-sort:hover {
    cursor: pointer;
  }
</style>
